// Boundaries are GeoJSON Polygon features. Their pagination the list
// endpoint is not an array, but a FeatureCollection.

import { z } from "zod";

import { fetchWithError, useParsedQueries, useParsedQuery } from "@nens/react-query-utils";
import { combineUrlAndParams } from "@nens/shared-utils";

import { featureSchema, polygonSchema } from "../geojson";
import { geojsonPaginatedSchema } from "../pagination";

export const boundarySchema = featureSchema.extend({
  id: z.number(),
  geometry: polygonSchema,
  properties: z.object({
    code: z.string(),
    name: z.string(),
    type: z.string() /* Actually an enum, but with quite a lot of fields */,
  }),
});

export type Boundary = z.infer<typeof boundarySchema>;

export const boundaryCollectionSchema = geojsonPaginatedSchema(boundarySchema);
export type BoundaryList = z.infer<typeof boundaryCollectionSchema>;

export function useBoundaries(params = {}) {
  const url = combineUrlAndParams("/api/v4/boundaries/", params);
  return useParsedQuery<BoundaryList>(
    ["lizard-api-v4", "boundaries", url],
    () => fetchWithError(url),
    (json: unknown) => boundaryCollectionSchema.parse(json)
  );
}

export function useBoundary(id: number) {
  return useParsedQuery<Boundary>(
    ["lizard-api-v4", "boundaries", id],
    () => fetchWithError(`/api/v4/boundaries/${id}/`),
    (json: unknown) => boundarySchema.parse(json)
  );
}

export function useMultipleBoundaries(ids: number[]) {
  return useParsedQueries(
    ids.map((id) => {
      return {
        queryKey: ["lizard-api-v4", "boundaries", id],
        fetchFunction: () => fetchWithError(`/api/v4/boundaries/${id}/`),
        parseFunction: (json: unknown) => boundarySchema.parse(json),
      };
    })
  );
}
